import { capitalize, identity, noop } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';
import styled from 'styled-components';
import { NONE, UNKNOWN_CROP_NAME } from '../../constants/fields';
import {
  colorForCrop,
  cropThemes,
  defaultCropColorForTheme,
} from '../../utils/colors';
import { useValueWidth } from '../../utils/hooks';
import DropdownButton from '../basics/DropdownButton';
import { useUnits } from '../../contexts/UnitContext';
import { PanelSection } from '../SidePanel';
import FormInput from '../basics/FormInput';
import ChevronIcon from '../icons/ChevronIcon';
import IconButton from '../basics/IconButton';
import ClickToCopyButton from '../basics/ClickToCopyButton';
import IconContainer from '../basics/IconContainer';
import { SquareSwatch } from './SquareSwatch';

const EmptyCropSwatch = styled(({ className }) => (
  <span className={className}>×</span>
))`
  display: flex;
  justify-content: center;
  width: 12px;
  height: 12px;
  padding-top: 2px;
  background-color: ${({ color }) => color};
  border: 0.5px solid ${({ borderColor }) => borderColor};
`;

// TODO: adapt this component to use the new <ColorDropdown /> component
// onSelect is a function which takes in a color object
export const CropSwatchPicker = styled(
  ({ className, crop, colorOptions, onSelect, readonly, ...props }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // if the crop is unknown, don't allow editing of color
    if (readonly || crop?.name === UNKNOWN_CROP_NAME) {
      return (
        <SquareSwatch
          color={colorForCrop(crop, cropThemes.LIGHT)}
          borderColor={colorForCrop(crop, cropThemes.DARK)}
          className={`ml-1 mr-2 ${className}`}
        />
      );
    }
    if (crop?.name === NONE) {
      return (
        <EmptyCropSwatch
          borderColor={defaultCropColorForTheme(cropThemes.DARK)}
          className={`ml-1 mr-2 ${className}`}
        />
      );
    }

    const toggle = () => setDropdownOpen((open) => !open);

    return (
      <Dropdown isOpen={dropdownOpen} toggle={toggle} className={className}>
        <DropdownToggle size="sm" className="p-1" {...props}>
          <SquareSwatch
            color={colorForCrop(crop, cropThemes.LIGHT)}
            borderColor={colorForCrop(crop, cropThemes.DARK)}
          />
        </DropdownToggle>
        {/* The container and flip props are needed to correctly position the open menu
      within the overflow: scroll table and prevent clipping. */}
        <DropdownMenu container="body" flip={false}>
          <DropdownItem key="header" header>
            Colors
          </DropdownItem>
          {colorOptions.map((color) => (
            <DropdownItem key={color.id} onClick={() => onSelect(color)}>
              <div className="d-flex align-items-center">
                <SquareSwatch
                  color={`#${color.hexLight}`}
                  borderColor={`#${color.hexDark}`}
                  className="mr-2"
                />
                <span
                  className={
                    color.name === crop?.color?.name ? 'font-weight-bold' : ''
                  }
                >
                  {capitalize(color.name)}
                </span>
              </div>
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    );
  },
)`
  align-items: center;
  flex-shrink: 0;
`;

const LABEL_MIN_WIDTH = 70;
const LABEL_MARGIN_RIGHT = 10;
export const labelMinDisplayWidth = LABEL_MIN_WIDTH + LABEL_MARGIN_RIGHT;

export const Label = styled(({ className, children }) => (
  <div className={`${className} font-tiny text-muted`}>{children}</div>
))`
  min-width: ${LABEL_MIN_WIDTH}px;
  margin-right: ${LABEL_MARGIN_RIGHT}px;
  ${({ $hasIcon }) => $hasIcon && `min-width: 88px;`};
  ${({ minWidth }) => minWidth && `min-width: ${minWidth}px;`};
  display: flex;
  align-items: center;
`;

export const LabeledData = styled(
  ({
    label,
    labelMinWidth,
    formatter = identity,
    unit,
    unitSpec,
    children,
    className,
    icon,
    copyable = false,
    ...rest
  }) => {
    const [hovered, setHovered] = useState(false);
    const { displayUnit: unitFromSpec, transformToUnit = identity } = useUnits(
      unitSpec,
    );
    const formattedValue = formatter(transformToUnit(children || 0));
    const displayUnit =
      unitFromSpec || (typeof unit === 'function' ? unit(children) : unit);
    const valueWithUnit = (
      <>
        {formattedValue}
        {displayUnit && (
          <>
            &nbsp;<span className="text">{displayUnit}</span>
          </>
        )}
      </>
    );

    return (
      <div
        className={className}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        {...rest}
      >
        <Label $hasIcon={!!icon} minWidth={labelMinWidth}>
          {icon && <IconContainer className="mr-2">{icon}</IconContainer>}
          {label}
        </Label>
        {copyable && hovered ? (
          <ClickToCopyButton
            buttonClassName="px-2"
            copyValue={[formattedValue, displayUnit].filter((v) => v).join(' ')}
            iconSize="13px"
          >
            {valueWithUnit}
          </ClickToCopyButton>
        ) : (
          valueWithUnit
        )}
      </div>
    );
  },
)`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  ${DropdownButton} {
    padding: 5px;
  }
`;

export const CropVarietyDropdown = ({
  isOpen = false,
  onClick = noop,
  plural = false,
}) => (
  <IconButton
    className="text-muted p-0"
    color="text"
    size="sm"
    onClick={onClick}
    $inline
  >
    {plural ? 'Varieties' : 'Variety'}
    <ChevronIcon direction={isOpen ? 'up' : 'down'} />
  </IconButton>
);

const Unit = styled.span`
  left: calc(${({ valueWidth }) => valueWidth}px + 2px);
  pointer-events: none;
  position: absolute;
  padding: 2px;
`;

const MenuInput = styled(
  ({
    defaultValue,
    normalize,
    update,
    isValid,
    onBlur = noop,
    onKeyUp = noop,
    onChange = noop,
    type,
    unitSpec,
    ...props
  }) => {
    const inputRef = useRef(null);
    useEffect(() => {
      if (inputRef.current) inputRef.current.value = defaultValue ?? '';
    }, [defaultValue]);
    return (
      <FormInput
        tag="input"
        innerRef={inputRef}
        type={type}
        {...props}
        onBlur={(e) => {
          // ideally we'd normalize and blur onChange
          // but this doesn't work for typed values (normalizing while typing can get wonky)
          // so, do it here
          const normalized = normalize(e.target.value);
          if (!isValid(normalized)) {
            e.target.value = defaultValue ?? ''; // Fallback on empty string here to avoid trying to set it to `undefined`
          } else {
            // always set value = normalized
            // numbers that are the equivalent but display differently should be normalized on blur
            e.target.value = normalized;
            if (normalized !== defaultValue) {
              update(normalized);
            }
          }
          // Call this after so that the width adjusts to the new value for LabeledData
          onBlur(e);
        }}
        onKeyUp={(e) => {
          onKeyUp(e);
          if (e.code === 'Enter') {
            e.target.blur();
          }
        }}
        onChange={(e) => {
          onChange(e);
          // this is an arrow key/button in a numeric field
          // and we should update the section value immediately
          // to provide the user immediate feedback
          if (
            type === 'number' &&
            (e.nativeEvent?.inputType == null ||
              e.nativeEvent?.inputType === 'insertReplacementText')
          ) {
            const normalized = normalize(e.target.value);
            if (!isValid(normalized)) return;
            update(normalized);
            e.target.value = normalized;
          }
        }}
        defaultValue={defaultValue}
      />
    );
  },
)`
  padding-left: 5px;
  margin-left: -5px;
  background-color: var(--side-panel-color);
  border-radius: 3px;
  border: 1px solid var(--side-panel-color);

  &:hover,
  &:focus {
    border-color: var(--border-color);
    background-color: #fff;
    & + ${Unit} {
      color: var(--text-muted-color);
    }
  }
`;

export const LabeledInput = ({
  readonly,
  label,
  formatter,
  defaultValue,
  unit,
  unitSpec,
  update,
  icon,
  ...props
}) => {
  const [valueWidth, setValueForWidth] = useValueWidth(defaultValue);

  // Prevent units from interfering with placeholder:
  if (defaultValue == null) {
    // eslint-disable-next-line no-param-reassign
    unitSpec = undefined;
  }

  const {
    transformToUnit = identity,
    transformToDefault = identity,
    displayUnit = unit,
  } = useUnits(unitSpec);

  const labeledDataProps = readonly ? { unit, unitSpec, formatter } : {};
  const initialValue = transformToUnit(defaultValue);

  useEffect(() => {
    // Updating defaultValue doesn't trigger the onChange handler,
    // so we need to update it manually here.
    setValueForWidth(initialValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  return (
    <LabeledData label={label} icon={icon} {...labeledDataProps}>
      {readonly ? (
        initialValue
      ) : (
        <div className="position-relative">
          <MenuInput
            defaultValue={initialValue}
            onChange={(e) => setValueForWidth(e.target.value)}
            onBlur={(e) => setValueForWidth(e.target.value)}
            update={(v) => update(transformToDefault(v))}
            {...props}
          />
          {displayUnit && <Unit valueWidth={valueWidth}>{displayUnit}</Unit>}
        </div>
      )}
    </LabeledData>
  );
};

export const SectionHiddenWarning = () => (
  <PanelSection className="font-small text-muted">
    Section is hidden
  </PanelSection>
);
